/**
 * Process Error Boundary
 * 
 * Provides error isolation per-process to prevent one bad process from crashing the entire UI.
 * Catches errors during process lifecycle and provides user-friendly error handling.
 */

import { bus } from '../core/events.js';
import type { Process } from './process.js';
import type { ProcessContext } from './process.js';

/**
 * Process error information
 */
export interface ProcessError {
  processId: string;
  kind: string;
  error: Error;
  timestamp: number;
  context: string; // Where error occurred: 'start', 'stop', 'update', 'view', etc.
}

/**
 * Process Error Boundary
 * 
 * Wraps process operations to catch and isolate errors.
 * When a process fails, it's isolated and the user is shown a friendly error message.
 */
export class ProcessErrorBoundary {
  private failedProcesses: Map<string, ProcessError> = new Map();

  /**
   * Execute a process operation with error boundary
   */
  catchError(
    processId: string,
    kind: string,
    context: string,
    operation: () => void
  ): void {
    try {
      operation();
      // Success - clear any previous error for this process
      if (this.failedProcesses.has(processId)) {
        this.failedProcesses.delete(processId);
        bus.emit('process.recovered', {
          processId,
          kind
        });
      }
    } catch (error) {
      // Isolate error: don't let it crash the app
      const processError: ProcessError = {
        processId,
        kind,
        error: error instanceof Error ? error : new Error(String(error)),
        timestamp: Date.now(),
        context
      };

      this.failedProcesses.set(processId, processError);

      // Emit error event (processes can listen to show error UI)
      bus.emit('process.error', {
        processId,
        kind,
        error: processError.error.message,
        context
      });
    }
  }

  /**
   * Check if a process has failed
   */
  hasFailed(processId: string): boolean {
    return this.failedProcesses.has(processId);
  }

  /**
   * Get error for a process
   */
  getError(processId: string): ProcessError | null {
    return this.failedProcesses.get(processId) || null;
  }

  /**
   * Clear error for a process (manual recovery)
   */
  clearError(processId: string): void {
    this.failedProcesses.delete(processId);
  }
}

/**
 * Global process error boundary instance
 */
export const processErrorBoundary = new ProcessErrorBoundary();

